import { useMemo } from 'react'
import { Logo } from '@/common'
import { EntityConfig } from './FinalizedReportPreviewEmbedded'
import useI18n from '@/shared/hooks/useI18n'

export default function FinalizedReportCoverPage({
  operatorName,
  cnCodes,
  startDate,
  endDate,
  productionRouteName,
  finalizedDate,
  entityConfig,
  selectedPdfLanguage,
  isPreview
}: {
  startDate: string
  endDate: string
  creationDate: string
  operatorName: string
  finalizedDate: string
  cnCodes: {
    methodCode: string
    cnCode: string
  }[]
  productionRouteName: string
  entityConfig: EntityConfig
  selectedPdfLanguage?: string
  isPreview?: boolean
}) {
  const { t } = useI18n()
  const dateLongFormatterMonthYearForLang = useMemo(() => {
    return new Intl.DateTimeFormat(selectedPdfLanguage, {
      month: 'short',
      year: '2-digit'
    }).format
  }, [selectedPdfLanguage])

  const tLng = { lng: selectedPdfLanguage }
  return (
    <div
      className={`break-after-page min-h-[100vh] flex flex-col shadow-lg print:shadow-none justify-center items-center text-center bg-white page-padding ${
        isPreview ? 'h-[1122px] w-[794px] mx-auto' : ''
      }`}
    >
      <h1 className="font-bold text-lg">{operatorName}</h1>
      <p className="mt-2 text-text-secondary">
        {t('finalizedReportDocument:cover.reportingPeriod', tLng)}
        <br />
        {t('finalizedReportDocument:cover.fromTo', {
          ...tLng,
          startDate: dateLongFormatterMonthYearForLang(new Date(startDate)),
          endDate: dateLongFormatterMonthYearForLang(new Date(endDate))
        })}
      </p>
      <p className="italic text-xs mt-2 text-text-secondary">
        {t('finalizedReportDocument:cover.reportCreatedOn', { ...tLng, date: finalizedDate })}
      </p>
      <p className="text-xs mt-2 text-text-secondary mx-44 text-center">
        {t('finalizedReportDocument:cover.preparedInAccordance', tLng)}
      </p>
      <div className="mt-10 w-full px-32">
        <table className="w-full border-b">
          <thead>
            <tr className="text-left">
              <th className="py-2 px-2 font-normal text-text-secondary text-center">
                {t('finalizedReportDocument:cover.table.cnCode', tLng)}
              </th>
              <th className="py-2 px-2 font-normal text-text-secondary text-center">
                {t('finalizedReportDocument:cover.table.productionRoute', tLng)}
              </th>
              <th className="py-2 px-2 font-normal text-text-secondary text-center">
                {t('finalizedReportDocument:cover.table.productionMethod', tLng)}
              </th>
            </tr>
          </thead>
          <tbody className="bg-background-main">
            {cnCodes.map(item => (
              <tr key={item.methodCode} className="border-t">
                <td className="py-2 px-2 font-semibold">{item.cnCode}</td>
                <td className="py-2 px-2 font-semibold">{productionRouteName}</td>
                <td className="py-2 px-2 font-semibold">
                  {t(`common:productionMethods.${item.methodCode}`, tLng)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="flex flex-col items-center justify-center mt-32">
        <Logo className="w-32 h-12" />
        <p className="text-xs">
          {entityConfig.entityName} - {entityConfig.street}
          <br />
          {entityConfig.city}, {entityConfig.country}
        </p>
      </div>
    </div>
  )
}
